import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ButtonPrimary,
  ButtonSecondary,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@americanexpress/dls-react';

export const DEFAULT_CONSENT_CONTENT = {
  title: 'Your Consent to Electronic Delivery ("Consent Statement")',
  lastModified: 'June 01, 2016',
  sections: [
    {
      heading: 'Items Covered By This Consent Statement',
      paragraphs: [
        'You have the right to receive your Card application decision notices to inform you, for example, whether your Card application has been approved, declined or canceled due to missing information (together, the "Covered Items") in paper form by mail. We may instead provide these Covered Items to you electronically if you: give us your Consent to do so and satisfy the System Requirements below.',
      ],
    },
    {
      heading: 'Consent',
      paragraphs: [
        'By agreeing to this Consent Statement, you give us your consent to electronically provide you the Covered Items and you certify you meet our System Requirements.',
      ],
    },
    {
      heading: 'Methods of Providing Covered Items',
      paragraphs: [
        'In this document, "provide" means to deliver, make available, send, notify or similar term. We may provide the Covered Items electronically through any combination of (1) links provided through our website or (2) posting the Covered Items on our website. It is your responsibility to review Covered Items promptly, so you can take appropriate action.',
      ],
    },
    {
      heading: 'Access to Paper Copies',
      paragraphs: [
        'Save or print copies of Covered Items to ensure you have them when needed. We retain copies of these Covered Items for the time periods required by law and will provide you with copies of these upon request within those time periods. We do not necessarily retain copies for longer than is required by law.',
      ],
    },
    {
      heading: 'Our Right to Send Paper',
      paragraphs: [
        'We reserve the right to provide Covered Items in paper form at all times at our discretion even if you have given us Consent to provide them electronically.',
      ],
    },
    {
      heading: 'System Requirements',
      paragraphs: [
        'To access and retain the Covered Items, you must have a computing or communications device with:',
      ],
      bullets: [
        'working Internet access,',
        'a Web browser that supports 128-bit encryption (such as Chrome, Firefox, Internet Explorer, or Safari), and',
        '16 MB of available memory (32 MB of RAM recommended).',
      ],
      footer:
        'By providing us your Consent, you certify you meet all of the above System Requirements.',
    },
  ],
};

const wrapLine = (line, maxChars = 94) => {
  if (!line || line.length <= maxChars) {
    return [line];
  }

  const words = line.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const next = currentLine ? `${currentLine} ${word}` : word;
    if (next.length > maxChars) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }
    currentLine = next;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const escapePdfText = (value = '') =>
  value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

const buildContentLines = (content) => {
  const lines = [content.title, `Last Modified: ${content.lastModified}`, ''];

  content.sections.forEach((section) => {
    lines.push(section.heading);
    section.paragraphs.forEach((paragraph) => {
      wrapLine(paragraph).forEach((line) => lines.push(line));
    });

    if (section.bullets) {
      section.bullets.forEach((bullet) => {
        wrapLine(`- ${bullet}`).forEach((line) => lines.push(line));
      });
    }

    if (section.footer) {
      wrapLine(section.footer).forEach((line) => lines.push(line));
    }

    lines.push('');
  });

  return lines;
};

export const generateConsentPdfBlob = (content) => {
  const lines = buildContentLines(content);
  const contentOperations = ['BT', '/F1 10 Tf', '40 760 Td'];

  lines.forEach((line, index) => {
    if (index > 0) {
      contentOperations.push('0 -14 Td');
    }
    contentOperations.push(`(${escapePdfText(line)}) Tj`);
  });

  contentOperations.push('ET');
  const streamContent = `${contentOperations.join('\n')}\n`;

  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj\n',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj\n',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >> endobj\n',
    `4 0 obj << /Length ${streamContent.length} >> stream\n${streamContent}endstream endobj\n`,
    '5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj\n',
  ];

  let pdf = '%PDF-1.4\n';
  const offsets = [0];

  objects.forEach((objectString) => {
    offsets.push(pdf.length);
    pdf += objectString;
  });

  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';

  for (let index = 1; index <= objects.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  }

  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

const ConsentStatement = ({ isOpen, onClose, consentContent }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const content = useMemo(
    () => consentContent || DEFAULT_CONSENT_CONTENT,
    [consentContent],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    try {
      const blob = generateConsentPdfBlob(content);
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl);
      setIsPdfReady(true);
      setPdfError('');

      return () => {
        if (typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(objectUrl);
        }
      };
    } catch {
      setIsPdfReady(false);
      setPdfError('We could not generate the consent PDF right now.');
      return undefined;
    }
  }, [isOpen, content]);

  const handleSave = () => {
    if (!pdfUrl) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.download = 'consent-statement.pdf';
    anchor.click();
  };

  const handlePrint = () => {
    if (!pdfUrl) {
      return;
    }

    const printWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    if (printWindow) {
      const handlePrintWindowLoad = () => {
        printWindow.focus();
        printWindow.print();
      };

      if (typeof printWindow.addEventListener === 'function') {
        printWindow.addEventListener('load', handlePrintWindowLoad, {
          once: true,
        });
      } else {
        handlePrintWindowLoad();
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      id="consent-statement-modal"
      aria-labelledby="consent-statement-heading"
      aria-describedby="consent-statement-content"
      onClose={onClose}
    >
      <ModalHeader
        id="consent-statement-heading"
        closeButtonLabel="Close consent statement"
        theme={{
          position: 'relative',
          justifyContent: 'center',
          minHeight: '3.5rem',
        }}
      >
        <h2 className="fluid heading-4">{content.title}</h2>
      </ModalHeader>

      <ModalBody
        id="consent-statement-content"
      >
        <div className="pad-4">
          <p className="body-2 pad-2-b">
            <strong>Last Modified:</strong> {content.lastModified}
          </p>

          {content.sections.map((section) => (
            <div key={section.heading}>
              <Heading level={3} headingStyle="heading5" className="pad-2-b">
                {section.heading}
              </Heading>

              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="body-4 pad-2-b">
                  {paragraph}
                </p>
              ))}

              {section.bullets ? (
                <ul className="pad-2-b margin-2-l">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="body-4 pad-1-b">
                      {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}

              {section.footer ? (
                <p className="body-2 mar-0">{section.footer}</p>
              ) : null}
            </div>
          ))}

          {pdfError ? (
            <p className="mar-2-t mar-0-b dls-color-warning" role="alert">
              {pdfError}
            </p>
          ) : null}

          {/* Uncomment and Implement when ready to call PDF from source */}
          {/* {pdfUrl ? (
            <div className="pad-2-t">
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setPageCount(numPages)}
                onLoadError={() =>
                  setPdfError(
                    'Unable to preview PDF. You can still save or print.',
                  )
                }
              >
                {Array.from({ length: pageCount }).map((_, index) => (
                  <Page
                    key={`consent-pdf-page-${index + 1}`}
                    pageNumber={index + 1}
                    width={760}
                  />
                ))}
              </Document>
            </div>
          ) : null} */}
        </div>
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem',
          }}
        >
          <div>
            <ButtonSecondary
              type="button"
              size="sm"
              disabled={!isPdfReady}
              onClick={handleSave}
            >
              Save
            </ButtonSecondary>
          </div>
          <div>
            <ButtonSecondary
              type="button"
              size="sm"
              disabled={!isPdfReady}
              onClick={handlePrint}
            >
              Print
            </ButtonSecondary>
          </div>
          <div>
            <ButtonPrimary type="button" size="sm" onClick={onClose}>
              Close
            </ButtonPrimary>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

ConsentStatement.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  consentContent: PropTypes.shape({
    title: PropTypes.string,
    lastModified: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string,
        paragraphs: PropTypes.arrayOf(PropTypes.string),
        bullets: PropTypes.arrayOf(PropTypes.string),
        footer: PropTypes.string,
      }),
    ),
  }),
};

ConsentStatement.defaultProps = {
  isOpen: false,
  onClose: () => {},
  consentContent: DEFAULT_CONSENT_CONTENT,
};

export default ConsentStatement;
